import { Select } from 'antd'
import { SizeType } from 'antd/es/config-provider/SizeContext'
import { isBGM, isDistributor } from 'common/config/acl'
import { roleGetInitialData, useAppContext } from 'contexts/AppContext'
import useACL from 'hooks/useACL'
import useDebounce from 'hooks/useDebounce'
import useGetDataOwnerShip from 'hooks/useGetDataOwnerShip'
import useGetWarehouseID from 'hooks/useGetWarehouseIDs'
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from 'hooks/usePaging'
import { Distributor } from 'interfaces/distributor'
import { Warehouse } from 'interfaces/warehouse'
import { uniqBy } from 'lodash'
import { useTranslations } from 'next-intl'
import { stringify } from 'qs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import axiosInstance from 'utils/axiosInstance'
import {
  convertToArray,
  defaultStringifyOption,
  getResponseData,
} from 'utils/commonUtils'
import { DISTRIBUTOR_TYPE_MASTER_DISTRIBUTOR_HUB } from 'utils/distributor'

interface IProps {
  disabled?: boolean
  optionValueKey?: string
  value?: any
  multiple?: boolean
  onChange?: (id: string, options: any) => void
  placeholder?: string
  isGetHub?: boolean
  filters?: any
  initialValue?: Distributor[]
  isGetCode?: boolean
  dropdownMatchSelectWidth?: boolean
  keySearch?: string
  allowClear?: boolean
  isRequiredCallByGetCode?: boolean
  setDataDistributors?: (values: Distributor[]) => void
  size?: SizeType
  setSelectedDistributor?: (value: Distributor) => void
}

const DistributorSelect: React.FC<IProps> = ({
  disabled,
  optionValueKey = 'id',
  multiple = false,
  value,
  onChange,
  placeholder,
  isGetHub = false,
  filters,
  initialValue,
  isGetCode,
  dropdownMatchSelectWidth = false,
  allowClear = true,
  keySearch = 'name',
  isRequiredCallByGetCode = true,
  setDataDistributors,
  size = 'middle',
  setSelectedDistributor,
}) => {
  const [loading, setLoading] = useState(false)
  const [distributors, setDistributors] = useState<Distributor[]>([])
  const hasCalledSetDataDistributors = useRef(false)

  const { warehouseIDs } = useGetWarehouseID()
  const { userRoles } = useACL()
  const [searchTerm, setSearchTerm] = useState<string>()
  const translate = useTranslations()
  const debouncedSearchTerm = useDebounce(searchTerm, 500)
  const { warehouseOwnerShip } = useGetDataOwnerShip(!filters)
  const {
    state: { distributors: distributorsContext, distributor },
  } = useAppContext()

  const isRoleBGM = isBGM(userRoles)

  const isRoleDistributor = isDistributor(userRoles)

  const isRoleGetInitialDistributor = useMemo(
    () => userRoles.some((role) => roleGetInitialData.includes(role)),
    [userRoles]
  )

  const checkSearchValue = useMemo(
    () => isRoleBGM || initialValue || isRoleGetInitialDistributor,
    [isRoleBGM, initialValue, isRoleGetInitialDistributor]
  )

  useEffect(() => {
    if (
      distributors?.length > 0 &&
      setDataDistributors &&
      !hasCalledSetDataDistributors.current
    ) {
      setDataDistributors(distributors)
      hasCalledSetDataDistributors.current = true
    }
  }, [distributors])

  const getDistributors = async (name?: string) => {
    try {
      setLoading(true)
      const params = stringify(
        {
          page: DEFAULT_PAGE,
          perPage: DEFAULT_PAGE_SIZE,
          orderBy: 'updatedAt desc',
          code: isGetCode && isRequiredCallByGetCode ? value : undefined,
          warehouseIDs: warehouseOwnerShip?.map((item) => item?.id),
          ids: isRoleDistributor ? distributor?.id : undefined,
          ...filters,
          ...(isGetHub && {
            type: DISTRIBUTOR_TYPE_MASTER_DISTRIBUTOR_HUB,
            isActive: true,
          }),
          [keySearch]: name,
        },
        {
          encode: false,
          skipNulls: true,
        }
      )
      const response = await axiosInstance.get(`/v1/distributors?${params}`)
      const responseData = getResponseData<Distributor[]>(response)
      setDistributors(responseData)
    } catch (error) {
      setDistributors([])
    } finally {
      setLoading(false)
    }
  }

  const getWarehouses = async (ids: string[]) => {
    try {
      setLoading(true)
      const params = stringify(
        { ids, includeDistributor: true },
        defaultStringifyOption
      )
      const res = await axiosInstance.get(`/v1/warehouses?${params}`)
      const data = getResponseData<Warehouse[]>(res)

      const dataDistributors = uniqBy(
        data?.map(({ distributor }) => distributor),
        'id'
      )

      setDistributors(dataDistributors)
      setLoading(false)
    } catch (error) {
      setDistributors([])
      setLoading(false)
    }
  }

  useEffect(() => {
    if (initialValue) {
      setDistributors(initialValue)
      return
    }
    if (isRoleGetInitialDistributor) {
      setDistributors(distributorsContext ?? [])
      return
    }
    if (isRoleBGM && warehouseIDs?.length > 0 && !distributors.length) {
      getWarehouses(warehouseIDs)
      return
    }
    if (!isRoleBGM) {
      debouncedSearchTerm
        ? getDistributors(debouncedSearchTerm)
        : getDistributors()
    }
  }, [
    debouncedSearchTerm,
    isRoleBGM,
    warehouseIDs,
    warehouseOwnerShip,
    initialValue,
    isRoleGetInitialDistributor,
    distributorsContext,
    isRoleDistributor,
    distributor,
  ])

  const getDistributorByID = useCallback(async () => {
    const promises =
      multiple &&
      convertToArray(value)?.map((i) => {
        return axiosInstance.get(`/v1/distributors/${i}`).catch((err) => err)
      })

    const res: any =
      optionValueKey === 'id' && !multiple
        ? await axiosInstance.get(`/v1/distributors/${value}`)
        : await Promise.all(promises || [])

    const data =
      optionValueKey === 'id' && !multiple
        ? getResponseData<any>(res as any)
        : res?.map((i) => i?.data?.data)

    if (data) {
      if (multiple) {
        const nextItems = distributors.filter((dis) => {
          return data.find(({ id }) => id === dis.id)
        })

        if (nextItems.length !== data?.length) {
          setDistributors([...distributors, ...data])
        }
        if (typeof setSelectedDistributor === 'function') {
          setSelectedDistributor(data)
        }
      } else {
        const nextItems = distributors.filter((dis) => {
          return dis.id === data.id
        })

        if (nextItems.length === 0) {
          setDistributors([...distributors, data])
        }
        if (typeof setSelectedDistributor === 'function') {
          setSelectedDistributor(data)
        }
      }
    }
  }, [distributors, multiple, value])

  useEffect(() => {
    const existing =
      multiple && value?.length > 0
        ? distributors.find((i) => value?.includes(i.id))
        : distributors.find((i) => i.id === value)
    if (existing) {
      if (typeof setSelectedDistributor === 'function') {
        setSelectedDistributor(existing)
      }
    }
    if (
      !isGetCode &&
      ((value && !existing && !multiple) ||
        (multiple && !existing && value?.length > 0)) &&
      distributors?.length
    ) {
      getDistributorByID()
    }
  }, [value, multiple, distributors, isGetCode])

  return (
    <Select
      mode={multiple ? 'multiple' : undefined}
      allowClear={allowClear}
      showSearch
      size={size}
      value={value}
      disabled={disabled ?? isRoleDistributor}
      loading={loading}
      showArrow={false}
      className="w-full"
      placeholder={placeholder || translate('common.selectDistributor')}
      onBlur={() => setSearchTerm(undefined)}
      onChange={onChange}
      onClear={() => setSearchTerm(undefined)}
      onDeselect={() => setSearchTerm(undefined)}
      onSearch={checkSearchValue ? undefined : setSearchTerm}
      dropdownMatchSelectWidth={dropdownMatchSelectWidth}
      filterOption={
        checkSearchValue
          ? (input, option: any) => {
              const childrenValue = String(option?.children || '')
              return childrenValue.toLowerCase().includes(input.toLowerCase())
            }
          : false
      }
      maxTagCount="responsive"
    >
      {distributors?.map((p) => (
        <Select.Option key={p.id} value={p[optionValueKey]}>
          {p.name} {isGetCode ? ` (${p.code})` : ''}
        </Select.Option>
      ))}
    </Select>
  )
}

export default React.memo(DistributorSelect)
